import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, AppBar, Toolbar, Typography, Container, Tabs, Tab, Fab, Alert } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScienceIcon from '@mui/icons-material/Science';
import FlashOnIcon from '@mui/icons-material/FlashOn';

import { QuantumExperiment } from './components/QuantumExperiment';
import { TimeModem } from './components/TimeModem';

// Cyberpunk theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ff41',
    },
    secondary: {
      main: '#ff00ff',
    },
    background: {
      default: '#0a0a0a',
      paper: '#1a1a2e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#aaaaaa',
    },
  },
  typography: {
    fontFamily: '"Orbitron", "Roboto", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    h2: {
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    h3: {
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    h4: {
      fontWeight: 600,
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 'bold',
        },
      },
    },
  },
});

const matrixRain = keyframes`
  0% {
    transform: translateY(-100vh);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

const BackgroundEffect = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 20%, rgba(0, 255, 65, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, rgba(0, 255, 255, 0.1) 0%, transparent 50%);
    animation: ${matrixRain} 20s linear infinite;
  }
`;

const CronosTitle = styled(Typography)`
  background: linear-gradient(45deg, #00ff41, #00f5ff, #ff00ff);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `} 3s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(0, 255, 65, 0.5);
`;

const StatusIndicator = styled(Box)<{ active: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${props => props.active ? '#00ff41' : '#666'};
  z-index: 1000;
`;

const PulsingDot = styled(Box)<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? '#00ff41' : '#666'};
  animation: ${props => props.active ? keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  ` : 'none'} 1s ease-in-out infinite;
`;

interface QuantumDataPoint {
  timestamp: number;
  pattern: 'wave' | 'particle';
  bit: number;
}

function App() {
  const [currentTab, setCurrentTab] = useState(0);
  const [quantumData, setQuantumData] = useState<QuantumDataPoint[]>([]);
  const [systemActive, setSystemActive] = useState(false);
  const [criticalAlert, setCriticalAlert] = useState<string | null>(null);

  useEffect(() => {
    // Check if system is active
    setSystemActive(quantumData.length > 0);

    // Simulate critical alerts
    if (quantumData.length > 50 && Math.random() > 0.98) {
      setCriticalAlert('Temporal correlation anomaly detected - investigating...');
      setTimeout(() => setCriticalAlert(null), 5000);
    }
  }, [quantumData]);

  const handleQuantumData = (data: QuantumDataPoint) => {
    setQuantumData(prev => [...prev.slice(-999), data]);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <BackgroundEffect />
      
      <StatusIndicator active={systemActive}>
        <PulsingDot active={systemActive} />
        <Typography variant="caption" sx={{ color: systemActive ? '#00ff41' : '#666' }}>
          {systemActive ? 'QUANTUM LINK ACTIVE' : 'SYSTEM STANDBY'}
        </Typography>
      </StatusIndicator>

      {criticalAlert && (
        <Alert 
          severity="warning" 
          sx={{ 
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: 'rgba(255, 152, 0, 0.9)',
            border: '1px solid #ff9800'
          }}
        >
          {criticalAlert}
        </Alert>
      )}

      <AppBar position="static" sx={{ background: 'rgba(26, 26, 46, 0.9)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <ScienceIcon sx={{ mr: 2, color: '#00ff41' }} />
          <CronosTitle variant="h4" sx={{ flexGrow: 1 }}>
            CRONOS
          </CronosTitle>
          <Typography variant="subtitle2" sx={{ color: '#aaa' }}>
            Quantum Time Communication Research Platform
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', background: 'rgba(0, 0, 0, 0.5)' }}>
        <Tabs value={currentTab} onChange={handleTabChange} centered>
          <Tab 
            icon={<FlashOnIcon />} 
            label="Quantum Experiment" 
            sx={{ color: '#fff', '&.Mui-selected': { color: '#00ff41' } }}
          />
          <Tab 
            icon={<AccessTimeIcon />} 
            label="Time Modem" 
            sx={{ color: '#fff', '&.Mui-selected': { color: '#00f5ff' } }}
          />
        </Tabs>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {currentTab === 0 && (
          <QuantumExperiment onDataPoint={handleQuantumData} />
        )}
        
        {currentTab === 1 && (
          <TimeModem quantumData={quantumData} />
        )}
      </Container>

      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 2,
        }}
      >
        <Fab
          size="small"
          sx={{
            background: 'linear-gradient(45deg, #00ff41, #00a82d)',
            color: '#000',
            '&:hover': {
              background: 'linear-gradient(45deg, #00a82d, #00ff41)',
            }
          }}
          onClick={() => setCurrentTab(0)}
        >
          <FlashOnIcon />
        </Fab>
        
        <Fab
          size="small"
          sx={{
            background: 'linear-gradient(45deg, #00f5ff, #0077ff)',
            color: '#000',
            '&:hover': {
              background: 'linear-gradient(45deg, #0077ff, #00f5ff)',
            }
          }}
          onClick={() => setCurrentTab(1)}
        >
          <AccessTimeIcon />
        </Fab>
      </Box>

      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          p: 1,
          borderTop: '1px solid #333',
        }}
      >
        <Typography variant="caption" sx={{ color: '#666', textAlign: 'center', display: 'block' }}>
          THEORETICAL RESEARCH PLATFORM • NO ACTUAL TIME TRAVEL • 
          Based on quantum mechanics research and delayed-choice quantum eraser experiments
        </Typography>
      </Box>
    </ThemeProvider>
  );
}

export default App;